import React from 'react'
import { Tooltip } from '@mui/material'
import { useSmartTranslation } from '../hooks/useSmartTranslation'
import { STEP_STATES, getStepState, getActiveStepIndex, getSubWorkflowLabel } from './partProgressUtils'

/* Above this many steps the per-step segments become unreadable dots that
   overflow the header block — switch to a continuous fill bar instead. */
const MAX_BAR_SEGMENTS = 24

/**
 * Left header block of the rail: eyebrow + optional part code, completed/total
 * STEPS fraction and a thin overall bar — one coloured segment per step on
 * short routes, a continuous completed/total fill on long ones. Without a part
 * code the eyebrow reads "Progress" — next to a bare fraction, "Part 0/3" was
 * misread as a piece count.
 */
export const PartProgressHeader = ({ steps, partCode = '', summary }) => {
  const { t_ } = useSmartTranslation()
  const activeIndex = getActiveStepIndex(steps)
  return (
    <div className='ppx-head'>
      <span className='ppx-head__eyebrow'>{partCode ? t_('Part') : t_('Progress')}</span>
      <div className='ppx-head__row'>
        {partCode ? <span className='ppx-head__code'>{partCode}</span> : ''}
        <span className={'ppx-head__fraction' + (summary.failed > 0 ? ' ppx-head__fraction--failed' : '')}>
          {summary.completed}/{summary.total}
        </span>
      </div>
      {steps.length <= MAX_BAR_SEGMENTS
        ? (
          <div className='ppx-head__bar'>
            {steps.map((step, index) => (
              <span
                key={index}
                className={'ppx-head__bar-segment ppx-head__bar-segment--' + getStepState(step, index, activeIndex)}
              />
            ))}
          </div>
          )
        : (
          <div className='ppx-head__bar ppx-head__bar--continuous'>
            <span
              className='ppx-head__bar-fill'
              style={{ width: (summary.total > 0 ? Math.round((summary.completed / summary.total) * 100) : 0) + '%' }}
            />
          </div>
          )}
    </div>
  )
}

/**
 * Same hover details as the original PartProgressStepper (asset / running task /
 * running program), plus the subworkflow the step belongs to, when known.
 */
export const StepTooltip = ({ step, children }) => {
  const { t_ } = useSmartTranslation()
  const subWorkflowName = getSubWorkflowLabel(step)
  const rows = [
    [step.technologyName, step.name].filter(Boolean).join(' · '),
    subWorkflowName ? t_('Subworkflow') + ': ' + subWorkflowName : '',
    step.assetName ? t_('Asset name') + ': ' + step.assetName : '',
    step.runningTaskName ? t_('Running task') + ': ' + step.runningTaskName : '',
    step.programName ? t_('Running program') + ': ' + step.programName : ''
  ].filter(Boolean)

  if (rows.length === 0) {
    return children
  }
  return (
    <Tooltip
      title={
        <div style={{ whiteSpace: 'nowrap' }}>
          {rows.map((row, index) => <div key={index}>{row}</div>)}
        </div>
      }
    >
      {children}
    </Tooltip>
  )
}

/** Small per-state glyph shown inside the chips. */
export const stateGlyph = (state) => {
  if (state === STEP_STATES.PASSED) return '✓'
  if (state === STEP_STATES.FAILED) return '✕'
  if (state === STEP_STATES.SKIPPED) return '–'
  return ''
}
